import { action, autorun, computed, makeAutoObservable, reaction } from 'mobx';

import { ContentListProviders, GetContentInfoParams } from '@smart-tv/typings/content';
import { EpisodeExtended } from '@smart-tv/components/program-sheet/types';
import getContentInfo, { ContentInfoRaw } from '@smart-tv/utils/kmm/getContentInfo';

import { RootStore } from './store-root';
import { uiStore } from './store-ui';

class SheetStore {
  rootStore: RootStore;
  sheet = {} as ContentInfoRaw;
  params = {} as GetContentInfoParams;
  loading = true;
  isPlayerOpen = false;
  isRefreshPending = false;
  checkNetworkCallback?: () => void | Promise<void> | Promise<boolean>;
  refreshSheetTimeout: NodeJS.Timeout | null = null;
  fetchGeneration = 0;

  constructor(root: RootStore) {
    this.rootStore = root;
    makeAutoObservable(this, {
      setParams: action,
      setSheet: action,
      resetSheet: action,
      setLoading: action,
      setIsCustomerFavorite: action,
      setIsPlayerOpen: action,
      setIsRefreshPending: action,
      cast: computed,
      directors: computed,
      supplierLogo: computed
    });

    let fetchTimeout: NodeJS.Timeout | null = null;

    const debouncedFetch = () => {
      this.setIsRefreshPending(true);
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
      }
      fetchTimeout = setTimeout(() => {
        this.fetchSheet();
        fetchTimeout = null;
      }, 50);
    };

    // Fetch when params are ready and player is not open
    autorun(() => {
      if (this.params.fetchReady && !this.isPlayerOpen) {
        debouncedFetch();
      }
    });

    // Refetch when player closes
    reaction(
      () => this.isPlayerOpen,
      (isPlayerOpen, wasPlayerOpen) => {
        if (wasPlayerOpen && !isPlayerOpen && this.params.fetchReady) {
          debouncedFetch();
        }
      }
    );

    autorun(() => {
      if (this.sheet.providers) {
        const providers: EpisodeExtended['providers'] = JSON.parse(
          JSON.stringify(this.sheet.providers)
        );

        this.rootStore.providerVersionStore.setProvidersData(providers);
      }
    });
    autorun(() => {
      if (uiStore.toggles.focusEpisodes === false && this.sheet.providers) {
        const providers: EpisodeExtended['providers'] = JSON.parse(
          JSON.stringify(this.sheet.providers)
        );
        this.rootStore.providerVersionStore.setProvidersData(providers);
      }
    });
  }

  scheduleSheetRefreshAtTimeBoundary = (seconds: number) => {
    if (this.refreshSheetTimeout || this.isPlayerOpen) return;
    this.refreshSheetTimeout = setTimeout(
      () => {
        this.refreshSheetTimeout = null;
        this.fetchSheet();
      },
      //the 3 is just to ensure that the refresh happens after the time boundary (from not live -> live or from live -> not live), meaning we know the backend returns the correct state of product sheet
      (seconds + 3) * 1000
    );
  };

  cancelScheduledRefresh = () => {
    this.refreshSheetTimeout && clearTimeout(this.refreshSheetTimeout);
    this.refreshSheetTimeout = null;
  };

  setLoading = (payload: boolean) => {
    this.loading = payload;
  };

  setIsPlayerOpen = (payload: boolean) => {
    this.isPlayerOpen = payload;
    payload && this.cancelScheduledRefresh();
  };

  setIsRefreshPending = (payload: boolean) => {
    this.isRefreshPending = payload;
  };

  setCheckNetworkCallback = (callback: () => void | Promise<void> | Promise<boolean>) => {
    this.checkNetworkCallback = callback;
  };

  setSheet(payload: ContentInfoRaw) {
    this.sheet = payload;
    this.fetchGeneration++;
  }

  resetSheet() {
    this.sheet = {} as ContentInfoRaw;
  }

  refreshFocusEpisodes = async () => {
    const result = await getContentInfo(this.params);
    this.setSheet(result);
  };

  handleRemovedRecording = async (): Promise<boolean> => {
    try {
      const result = await getContentInfo(this.getTrimmedParams(), false);
      this.setSheet(result);

      if (result.episodeId) {
        await this.rootStore.seasonsStore.fetchSeasons({
          seriesId: result.contentId,
          eventId: this.params.eventId,
          sid: this.params.sid,
          providers: ContentListProviders.Subscribed,
          contentType: this.params.contentType,
          subProviderId: this.params.subProviderId,
          sheetData: result // Pass the already fetched sheet data
        });
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  setIsCustomerFavorite(value: boolean) {
    this.sheet.isCustomerFavorite = value;
  }
  setParams(params: GetContentInfoParams) {
    // Check if this is a meaningful parameter change that warrants a new fetch
    const isNewContent =
      params.contentId !== this.params.contentId ||
      params.sid !== this.params.sid ||
      params.eventId !== this.params.eventId ||
      params.contentType !== this.params.contentType ||
      params.universeId !== this.params.universeId ||
      params.providerCollectionId !== this.params.providerCollectionId ||
      params.subProviderId !== this.params.subProviderId;

    this.params = params;

    // Immediately show loading state for new content
    if (isNewContent && params.fetchReady) {
      this.setLoading(true);
      this.resetSheet();
      this.rootStore.episodesStore.resetFetchedEpisodes();
    }
  }

  getTrimmedParams(): GetContentInfoParams {
    if (!this.params.subProviderId) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const { subProviderId, ...paramsWithoutSubProviderId } = this.params;
      return paramsWithoutSubProviderId;
    }
    return this.params;
  }

  get cast() {
    if (!this.sheet.cast) {
      return [];
    }
    return this.sheet.cast.filter((item: any) => item.job === 'Skuespiller');
  }

  get directors() {
    if (!this.sheet.cast) {
      return [];
    }
    return this.sheet.cast.filter((item: any) => item.job === 'Instruktør');
  }

  get supplierLogo() {
    return this.sheet.providers[0]?.covers?.current;
  }

  *fetchSheet(): Generator<any, void, any> {
    if (this.params.contentId || (this.params.sid && this.params.eventId)) {
      this.rootStore.seasonsStore.resetSeasons();

      try {
        if (this.checkNetworkCallback) {
          yield this.checkNetworkCallback();
        }

        const result = yield getContentInfo(this.getTrimmedParams());

        this.setSheet(result);
        this.setLoading(false);

        if (result.episodeId) {
          this.rootStore.seasonsStore.fetchSeasons({
            seriesId: result.contentId,
            eventId: this.params.eventId,
            sid: this.params.sid,
            providers: ContentListProviders.Subscribed,
            contentType: this.params.contentType,
            subProviderId: this.params.subProviderId,
            sheetData: result // Pass the already fetched sheet data
          });
        }
      } catch (error) {
        this.setLoading(false); // Make sure to stop loading on error
      }
    }
    this.setIsRefreshPending(false);
  }
}

export default SheetStore;
